import React, { useState, useEffect } from 'react';
import { Phone, ArrowRight, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { BOOKING_URL, CTA_LABEL } from '../data/constants';

const links = [
  { id: 'intro', label: 'Was ist HIREKA' },
  { id: 'prozess', label: 'Ablauf' },
  { id: 'premium-plus', label: 'Preis' },
  { id: 'credits', label: 'Credit-System' },
  { id: 'about', label: 'Über uns' },
  { id: 'faq', label: 'FAQ' },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setOpen(false);
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        isScrolled || open ? 'glass-dark shadow-[0_10px_40px_-20px_rgba(0,0,0,0.8)]' : 'bg-transparent border-b border-transparent'
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-[72px] flex items-center justify-between">
        <a href="/" className="flex items-center" data-testid="header-logo">
          <img src="/hireka-logo.png" alt="HIREKA" className="h-9 brightness-0 invert" />
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              className="relative text-sm font-medium text-slate-300 hover:text-white transition-colors after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-amber-400 hover:after:w-full after:transition-all after:duration-300"
              data-testid={`nav-${l.id}`}
            >
              {l.label}
            </button>
          ))}
          <a href="/jobs" className="text-sm font-medium text-slate-300 hover:text-white transition-colors" data-testid="nav-jobs">
            Jobs
          </a>
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <a href="tel:+4915167026918" className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors" data-testid="header-phone">
            <Phone className="w-4 h-4 text-amber-400" />
            +49 151 670 269 18
          </a>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="btn-lux px-5 py-2.5 text-sm" data-testid="header-cta-btn">
            {CTA_LABEL}
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden text-white p-2 rounded-lg hover:bg-white/10 transition-colors"
          aria-label="Menü öffnen"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden border-t border-white/10"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-5 space-y-1">
              {links.map((l) => (
                <button key={l.id} onClick={() => scrollTo(l.id)} className="block w-full text-left text-slate-200 hover:text-white hover:bg-white/5 font-medium py-3 px-3 rounded-lg transition-colors">
                  {l.label}
                </button>
              ))}
              <a href="/jobs" className="block text-slate-200 hover:text-white hover:bg-white/5 font-medium py-3 px-3 rounded-lg transition-colors">Jobs</a>
              <div className="pt-4 mt-2 border-t border-white/10 space-y-3">
                <a href="tel:+4915167026918" className="flex items-center gap-3 text-slate-300 px-3">
                  <Phone className="w-4 h-4 text-amber-400" /> +49 151 670 269 18
                </a>
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="btn-lux w-full py-3.5 text-sm" data-testid="mobile-header-cta">
                  {CTA_LABEL} <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
